
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import ProjectsDocumentsFilteredList from "@/components/documents/ProjectsDocumentsFilteredList.vue";
  import { Document, Company, Project, User } from "@/generated/graphql";
  import RouteNames from "@/entities/RouteNames";
  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import DocumentsContractorProjectFilters from "@/components/documents/filters/DocumentsContractorProjectFilters.vue";
  import { cloneDeep, isEqual } from "lodash-es";
  import DocumentsContractorProjectsFilters from "@/entities/filters/DocumentsContractorProjectsFilters";
  import documentsContractorProjectsTemplate from "@/data/filtersTemplates/documentsContractorProjectsTemplate";
  import paginationConfig from "@/data/paginationConfig";
  import PaginationConfig from "@/entities/PaginationConfig";
  import LIST_DOCUMENTS from "@/apollo/documents/ListDocuments.gql";
  import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";
  import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import DocumentItem from "@/components/documents/DocumentItem.vue";
  import CardPagination from "@/components/card/CardPagination.vue";
  import CardBodyLink from "@/components/card/CardBodyLink.vue";
  import ActionsPanel from "@/components/common/ActionsPanel.vue";
  import EmptyListImageDocuments from "@/components/icons/emptyListImages/EmptyListImageDocuments.vue";
  import GET_COMPANIES from "@/apollo/company/GetCompanies.gql";

  @Component({
    components: {
      EmptyListImageDocuments,
      ActionsPanel,
      CardBodyLink,
      CardPagination,
      DocumentItem,
      LoadingOverlay,
      EmptyListPlaceholder,
      DocumentsContractorProjectFilters,
      DefaultContentLayout,
      ProjectsDocumentsFilteredList
    },
    apollo: {
      documents: {
        query: LIST_DOCUMENTS,
        variables() {
          return this.queryArgs;
        },
        update(data) {
          this.paginationConfig.total = data.total.aggregate.count;
          this.paginationConfig.loadedCount = data.document.length;
          return data.document;
        },
      },
      companies: {
        query: GET_COMPANIES,
        variables() {
          return this.companiesQuery;
        },
        update(data) {
          this.paginationCompanyConfig.total = data.total.aggregate.count;
          this.paginationCompanyConfig.loadedCount = data.companies.length;
          return data.companies;
        },
      },
    },
  })
  export default class DocumentsContractorProjects extends Vue {
    @Prop({ type: Object, required: true }) readonly currentUser!: User;
    companies?: Company[];
    RouteNames: typeof RouteNames = RouteNames;
    filters: DocumentsContractorProjectsFilters = cloneDeep(documentsContractorProjectsTemplate);
    paginationConfig: PaginationConfig = { ...paginationConfig };
    paginationCompanyConfig: PaginationConfig = { ...paginationConfig };
    documents: Document[] = [];

    get queryArgs() {
      const { page, limit } = this.paginationConfig;
      const nextOffset = page * limit - limit;
      return {
        offset: nextOffset,
        limit: limit,
        where: {
          _and: [
            // {
            //   company_id: { _in: this.companies?.map(company => company.id) || [] },
            // },
            {
              document_projects: {
                project_id: {
                  _is_null: false,
                },
                document:{
                  uploaded_by: { _eq: this.currentUser.id },
                }
              },
            },
            this.filtersConditions,
          ],
        },
      };
    }
    get companiesQuery(): Object {
      const { page, limit } = this.paginationCompanyConfig;
      const nextOffset = page * limit - limit;
      const params = {
        offset: nextOffset,
        limit: limit,
      };

      const conditions: any[] = [
        {
          contractors: {
            user_id: { _eq: this.currentUser.id },
            deleted: { _eq: false },
            approved: { _eq: true },
          },
        },
      ];


      return {
        where: { _and: conditions },
        ...params,
        userId: this.currentUser.id,
      };
    }
    get filtersConditions(): Record<string, any> {
      const conditions = [];

      if (this.filters.file_name.length > 0) {
        conditions.push({
          filename: {
            _ilike: `%${this.filters.file_name}%`,
          }
        });
      }

      if (this.filters.projects.length > 0) {
        conditions.push({
          document_projects: {
            project_id: {
              _in: this.filters.projects,
            },
          },
        });
      }

      if (this.filters.companies.length > 0) {
        conditions.push({
          document_projects: {
            project: {
              company_id: {
                _in: this.filters.companies,
              },
            },
          },
        });
      }

      if (conditions.length === 0) {
        return {};
      }

      return { _and: conditions };
    }

    get queryPropsToWatch(): string {
      return JSON.stringify([
        this.filters,
        this.paginationConfig,
      ]);
    }

    get queryParams(): { filters: DocumentsContractorProjectsFilters, pagination: PaginationConfig } {
      const query = this.$route.query;
      const filters = getFiltersDataFromQuery<DocumentsContractorProjectsFilters>(documentsContractorProjectsTemplate, query);
      const pagination = { ...this.paginationConfig, ...getPaginationDataFromQuery(query) };

      return {
        filters,
        pagination,
      };
    }

    get isLoading(): boolean {
      return this.$apollo.queries.documents.loading;
    }

    get isCollectionEmpty(): boolean {
      return this.documents.length === 0;
    }

    get isEmptyListPlaceholderShown(): boolean {
      return this.isCollectionEmpty && !this.isLoading && this.filtersEmpty;
    }

    get filtersEmpty(): boolean {
      return isEqual(this.filters, documentsContractorProjectsTemplate);
    }

    get isChosenPageEmpty(): boolean {
      const collectionEmpty = this.documents.length === 0;
      const notFirstPage = this.paginationConfig.page !== 1;

      return !this.isLoading && collectionEmpty && notFirstPage;
    }

    created(): void {
      const { filters, pagination } = this.queryParams;

      this.filters = filters;
      this.paginationConfig = pagination;
    }

    @Watch("queryPropsToWatch")
    updateQuery(): void {
      if (this.isChosenPageEmpty) {
        return this.showFirstPage();
      }

      const paginationQuery = getPaginationQuery(this.paginationConfig);

      const query = joinQueryValues({
        ...paginationQuery,
        ...this.filters,
      });

      if (isEqual(query, this.$route.query)) {
        return;
      }

      this.$router.push({ query });
    }

    getProject(document: Document): Project {
      return document.document_projects[0].project!;
    }

    updateFilters(filters: DocumentsContractorProjectsFilters): void {
      const dataEqual = isEqual(filters, this.filters);

      if (dataEqual) {
        return;
      }

      this.filters = filters;
    }

    showFirstPage(): void {
      this.paginationConfig = { ...this.paginationConfig, page: paginationConfig.page };
    }

    resetFilters(): void {
      if (isEqual(this.filters, documentsContractorProjectsTemplate)) {
        return;
      }

      this.filters = cloneDeep(documentsContractorProjectsTemplate);
      this.paginationConfig = { ...paginationConfig };
    }
  }
